"use client";

import { useEffect } from "react";
import { toast } from "sonner";
import { isStaleServerActionError } from "@/lib/stale-action";

/**
 * Global handler that catches stale-server-action errors and soft-reloads
 * the page so the browser picks up the post-deploy bundle.
 *
 * Background: when the app container is redeployed, Next.js bakes new
 * server-action hashes into the bundle. Any browser still showing a
 * pre-deploy page POSTs OLD hashes, and Next.js throws "Failed to find
 * Server Action ...". The error happens inside the Next.js dispatcher
 * BEFORE any downstream code runs — so retries inside triggerAIService,
 * auth, etc. can't help. The only durable fix is for the browser to drop
 * the cached bundle and re-fetch.
 *
 * Confirmed against the 2026-06-09 04:06 UTC deploy: 25 of these errors
 * landed in app-1 logs within 14h post-deploy, surfacing to users as
 * generic "AI service is unreachable" / "something went wrong" toasts
 * because the Server Action that wraps a call to AI service was the one
 * the dispatcher couldn't find.
 *
 * Behavior:
 *   1. First matching event:  show a warning toast + reload after 1.5s
 *   2. Subsequent events:     silently ignored (already reloading)
 *
 * The toast gives the user a beat to read what's happening; the short
 * delay is enough for the toast to render but short enough that a click-
 * impatient user won't hit the button again before reload fires.
 *
 * The component renders nothing — it just registers `error` and
 * `unhandledrejection` listeners on the window. Cleaned up on unmount
 * (in practice, never; it lives in Providers).
 */
export function StaleActionReloader() {
  useEffect(() => {
    // Reload-in-progress guard. Once we've triggered a reload, swallow
    // every subsequent stale-action event so the user doesn't get
    // toast-spammed by the 20+ retries Next.js fires when the cached
    // page has multiple buttons.
    let reloading = false;

    const triggerReload = () => {
      if (reloading) return;
      reloading = true;
      toast.warning("This page is out of date. Reloading…", {
        duration: 1500,
      });
      // Small delay so the toast has time to paint. Long enough to read,
      // short enough not to feel sluggish.
      window.setTimeout(() => {
        // Hard reload bypasses bfcache and any stale HTML the browser
        // might be holding. Plain location.reload() is sufficient on
        // modern browsers because the server now serves the new bundle.
        window.location.reload();
      }, 1500);
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isStaleServerActionError(event.reason)) {
        triggerReload();
      }
    };

    const onError = (event: ErrorEvent) => {
      if (isStaleServerActionError(event.error ?? event.message)) {
        triggerReload();
      }
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onError);

    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onError);
    };
  }, []);

  return null;
}
